import { Component, inject, OnInit, ViewChild } from '@angular/core';

import { CustomerService } from '../../services/customer.service';
import { VoucherService } from '../../services/voucher.service';
import { ProductService } from '../../services/product.service';
import { LicenseTemplateService } from '../../services/license-template.service';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PolicyService } from 'app/armp/services/policy.service';
import { from, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { FormArray, FormGroup, UntypedFormControl } from '@angular/forms';

import { InternalResourceService } from 'app/armp/services/internal-resource.service';
import { SystemService } from 'app/armp/services/system.service';
import { AuthService } from 'app/armp/core/auth.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { LoaderService } from 'app/armp/core/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../services/toast.service';
import { AutomaticUserBindingService } from 'app/armp/services/automaticuser-binding.service';
import { Product } from '../../models/product';
import { FormGroupContextBase } from '../voucher-atp/models/FormModels';
import { VoucherAtpHandlerService } from '../voucher-atp/service/voucher-atp-handler.service';
import { FormGroupClientInstallations } from '../voucher-client-installations/voucher-client-installations.component';
import { MatDialog } from '@angular/material/dialog';
import { ModalClientSecretComponent } from '../voucher-client-installations/modal-client-secret/modal-client-secret.component';
import { AtpSaveRequest } from '../../models/request/AtpSaveRequest';
import { AtpService } from '../../services/atp.service';
import { ArubaTrustedPlatformName } from '../../../../common/constants/constants';

@Component({
  selector: 'app-voucher',
  templateUrl: './voucher-create.component.html',
  styleUrls: ['./voucher-create.component.scss'],
  standalone: false
})
export class VoucherCreateComponent implements OnInit {
  private matDialog = inject(MatDialog);
  private atpService = inject(AtpService);

  defaults: any;
  systems: any;
  internalContact: any;

  product?: Product;
  products: Product[] = [];
  products$!: Observable<Product[]>;

  voucher: any = {};
  automaticUser: any;
  customer: any = {};
  customerSearchText: string;
  customerSearchResult: any[];

  licenseTemplates = [];
  selectedPolicies = {};
  policies = [];
  validity = false;
  tomorrow = new Date();
  today = new Date();
  licenseExpirationModes = [];
  newInternalContactVisible = false;
  internalContactDataSource: Observable<any>;
  internalContactFormControl = new UntypedFormControl();
  automaticSignatureDataSource: Observable<any>;
  automaticSignatureFormControl = new UntypedFormControl();
  certificates = [];
  canAddAutomaticUser = false;

  private _licenseTemplate: any = {};
  private licenseExpirationMode: any;
  private policyImplementations = [];
  private expiration = false;
  private searchingResource = false;

  formGroupClientInstallations?: FormGroup<FormGroupClientInstallations>;

  /** AMSFAAA-350 */
  arubaTrustedPlatformName = ArubaTrustedPlatformName.toLowerCase();
  formGroupAtp?: FormArray<FormGroup<FormGroupContextBase>>;

  @ViewChild('form', { static: false }) form;

  constructor(
    private customerService: CustomerService,
    public voucherService: VoucherService,
    private productService: ProductService,
    private licenseTemplateService: LicenseTemplateService,
    private location: Location,
    private router: Router,
    private policyService: PolicyService,
    private activatedRoute: ActivatedRoute,
    private internalResourceService: InternalResourceService,
    private systemService: SystemService,
    public authService: AuthService,
    private loaderService: LoaderService,
    private translator: TranslateService,
    private toastService: ToastService,
    private voucherAtpHandlerService: VoucherAtpHandlerService,
    private automaticUserBindingService: AutomaticUserBindingService
  ) {
    this.internalContactDataSource = this.internalContactFormControl.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap((text) => this.internalResourceService.search(text))
    );
    this.automaticSignatureDataSource = this.automaticSignatureFormControl.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap((text) => this.internalResourceService.search(text))
    );

    this.tomorrow.setDate(this.today.getDate() + 1);
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params) => {
      this.voucherService.getDefaults().then((defaults) => {
        this.defaults = defaults;
        this.voucher.maxReassignments = defaults.maxReassignments;
      });

      if (params.id) {
        this.voucherService.getById(+params.id).then((p) => {
          this.voucher = p;
          this.customerService.getById(this.voucher.customerId).then((c) => {
            this.customer = c;
          });

          this.productService.getById(this.voucher.productId).then((c) => {
            this.product = c;
            this.onProductChange();
          });
        });
      }
    });

    this.productService.getAll().then((response: Product[]) => {
      this.products = response;
    });

    this.products$ = from(this.productService.getAll());

    this.voucherService.getLicenseExpirationModes().then((response) => {
      this.licenseExpirationModes = response;
    });

    this.policyService.getImplementations().then((response) => {
      this.policyImplementations = response;
    });

    this.licenseTemplateService.getAll().then((response) => {
      this.licenseTemplates = response;
    });

    this.systemService.getAll().then((systems) => (this.systems = systems));
  }

  searchCustomer() {
    this.loaderService.show();
    this.customerService.search(this.customerSearchText).then((result) => {
      this.customerSearchResult = result;
      this.loaderService.hide();
    });
  }

  goToCustomerDetail(customer) {
    this.router.navigate(['/customer/' + customer.id]);
  }

  selectCustomer(customer) {
    this.customer = customer;
  }

  backToCustomerSearch() {
    this.customer = {};
    this.clearCustomerSearch();
  }

  onBack() {
    this.location.back();
  }

  clearCustomerSearch() {
    this.customerSearchResult = [];
    this.customerSearchText = null;
  }

  internalContactSelected(event: MatAutocompleteSelectedEvent) {
    this.internalContact = event.option.value;
    this.voucher.internalContactId = event.option.value.id;
    this.voucher.internalContactName = event.option.value.name;
    this.voucher.internalContactEmail = event.option.value.email;
    this.voucher.internalContactPhone = event.option.value.phone;
    this.newInternalContactVisible = false;
  }

  newInternalContact() {
    this.internalContact = null;
    this.voucher.internalContactId = null;
    this.newInternalContactVisible = true;
  }

  onProductChange() {
    if (this.product) {
      this.licenseTemplate = this.licenseTemplates.find((t) => t.id === this.product.licenseTemplateId);
    }

    if (!this.isArubaTrustedPlatformProduct()) {
      this.formGroupClientInstallations = undefined;
    }
  }

  onLicenseExpirationModeChange() {
    this.expiration = this.voucher.licenseExpirationMode == 'VOUCHER_EXPIRATION';
    this.validity = !(this.voucher.licenseExpirationMode == 'VOUCHER_EXPIRATION');
  }

  policyRemoved(policy) {
    if (policy.id) {
      this.selectedPolicies[policy.id] = false;
    }
  }

  onLicenseInstancePolicySelected(policy, selected: boolean) {
    if (selected) {
      let existing = this.policies.find((x) => x.id === policy.id);

      if (!existing) {
        existing = {};
        this.policies.push(existing);
      }

      existing = Object.assign(existing, policy);
      //existing['id'] = null;
    } else {
      this.policies.splice(
        this.policies.findIndex((x) => x.id === policy.id),
        1
      );
    }
  }

  /** AMSFAAA-350 */
  private checkFormGroupAtpIsValid(): boolean {
    if (this.formGroupAtp?.valid) {
      return true;
    } else {
      this.formGroupAtp?.markAllAsTouched();
      return false;
    }
  }

  private isArubaTrustedPlatformProduct(): boolean {
    return this.product?.name?.toLowerCase() === this.arubaTrustedPlatformName;
  }

  private checkFormGroupClientInstallationsIsValid(): boolean {
    if (!this.isArubaTrustedPlatformProduct()) {
      return true;
    }

    if (this.formGroupClientInstallations?.valid) {
      this.voucher.client_installation = this.formGroupClientInstallations.getRawValue();
      return true;
    }

    this.formGroupClientInstallations?.markAllAsTouched();
    return false;
  }

  save() {
    this.form.onSubmit(null);
    if (!this.form.valid || !this.checkFormGroupClientInstallationsIsValid()) {
      this.checkFormGroupAtpIsValid();
      return;
    }

    if (!this.isArubaTrustedPlatformProduct()) {
      this.updateVoucher();
      this.addUser();
      return;
    }

    if (!this.checkFormGroupAtpIsValid()) {
      return;
    }

    const atpSaveRequest: AtpSaveRequest = this.voucherAtpHandlerService.getAtpSaveRequest(this.formGroupAtp);

    this.loaderService.show();

    this.atpService.createAtpConfiguration(atpSaveRequest).subscribe({
      next: (res) => {
        this.voucher.client_configuration_id = res.client_configuration_id;
        this.updateVoucher();
        this.addUser();
      },
      error: () => {
        this.loaderService.hide();
      }
    });
  }

  private updateVoucher() {
    this.voucher.active = true;
    this.voucher.customerId = this.customer.id;
    this.voucher.productId = this.product.id;
    this.voucher.licenseTemplateId = this.licenseTemplate ? this.licenseTemplate.id : null;

    this.voucher.policies = this.policies.map((x) => {
      let tmp = {
        id: null,
        licenseTemplateId: null
      };
      Object.assign(tmp, x);
      if (tmp.licenseTemplateId) {
        tmp.id = null;
      }

      return tmp;
    });

    this.voucher.certificates = this.certificates.map((x) => {
      let tmp = {
        id: null
      };
      Object.assign(tmp, x);

      return tmp;
    });
  }

  private addUser() {
    if (this.automaticUser === undefined || this.automaticUser === null) {
      this.saveVoucher();
      return;
    }

    this.automaticUserBindingService
      .addUser(this.automaticUser)
      .then((createdUser: any) => {
        this.voucher.automaticSignatureUserId = createdUser.id;
        this.saveVoucher();
      })
      .catch(() => {
        this.loaderService.hide();
      });
  }

  private saveVoucher() {
    this.voucherService
      .save(this.voucher)
      .then((res) => {
        this.loaderService.hide();
        if (res['client_secret']) {
          this.showPopupClientSecret(res['client_secret']);
        } else {
          this.toastService.success({
            message: this.translator.instant('TOASTR.SAVE_SUCCESS')
          });
          this.onBack();
        }
      })
      .catch(() => {
        this.loaderService.hide();
      });
  }

  showPopupClientSecret(client_secret: string) {
    const dialogRef = this.matDialog.open(ModalClientSecretComponent, {
      data: { client_secret },
      disableClose: true,
      minWidth: 200,
      minHeight: 100
    });

    dialogRef.afterClosed().subscribe(() => {
      this.onBack();
    });
  }

  get licenseTemplate() {
    return this._licenseTemplate;
  }

  set licenseTemplate(licenseTemplate: any) {
    this._licenseTemplate = licenseTemplate;
    this.selectedPolicies = {};
    this.policies = [];
    if (licenseTemplate) {
      this.licenseTemplate.policies.forEach((element) => {
        this.selectedPolicies[element.id] = true;
        this.onLicenseInstancePolicySelected(element, true);
      });
    }
  }

  addAutomaticUser() {
    this.automaticUser = {};
    this.canAddAutomaticUser = true;
  }

  removeAutomaticUser() {
    this.canAddAutomaticUser = false;
    this.automaticUser = undefined;
  }
}

